// Game Over-logboek: filteren, groeperen, samenvatten en Nederlandse opmaak.
// Port van legacy/app.js sectie 8 (filterLogs, groupLogsByActivity, formatMomentDutch, formatDuurDutch).
import { getMainCategory, normalizeType } from './activities';
import type { LogEntry, MainCategory } from './types';

export type PeriodFilter = 'all' | 'today' | 'yesterday' | 'week';

export interface LogFilters {
  activity: string | 'all';
  type: 'all' | 'binnen' | 'buiten';
  category: MainCategory | 'all';
  period: PeriodFilter;
}

export const DEFAULT_LOG_FILTERS: LogFilters = { activity: 'all', type: 'all', category: 'all', period: 'all' };

export interface LogGroup {
  key: string;
  naam: string;
  type: LogEntry['type'];
  energie: string;
  categorie: MainCategory;
  activityId: number | null;
  count: number;
  totalMinutes: number;
  lastMoment: string;
  sessions: LogEntry[];
}

export interface LogSummary {
  /** Aantal beurten. */
  count: number;
  totalMinutes: number;
  /** Aantal beurten binnen. */
  binnen: number;
  /** Aantal beurten buiten. */
  buiten: number;
  /** Aantal beurten per hoofdcategorie. */
  byCategory: Record<MainCategory, number>;
}

/** Bovengrens per sessie: alles boven 24 uur is vrijwel zeker corrupte data. */
const MAX_MINUTES_PER_ENTRY = 24 * 60;
const MAX_TEXT = 120;

function cleanString(v: unknown, max = MAX_TEXT): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

function parseActivityId(v: unknown): number | null {
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  if (v === '') return null;
  const n = Number(v);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function parseMinutes(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.trim().replace(',', '.')) : NaN;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.round(n), MAX_MINUTES_PER_ENTRY);
}

const CATEGORY_KEYS = ['buiten-hoog', 'buiten-rustig', 'buiten-chill', 'binnen-hoog', 'binnen-rustig', 'binnen-chill'];

/**
 * Veilig parsen van bewaarde logs; ongeldige items overslaan.
 * Accepteert de ruwe array óf de JSON-string uit localStorage. Een item zonder geldig `moment`
 * of zonder positieve duur wordt overgeslagen; overige velden krijgen de legacy-standaardwaarden.
 */
export function parseStoredLogs(raw: unknown): LogEntry[] {
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(data)) return [];
  const out: LogEntry[] = [];
  const seenIds = new Set<string>();
  data.forEach((item: unknown, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return;
    const src = item as Record<string, unknown>;
    if (typeof src.moment !== 'string' && typeof src.moment !== 'number') return;
    const date = new Date(src.moment);
    if (Number.isNaN(date.getTime())) return;
    const duurMinuten = parseMinutes(src.duurMinuten);
    if (duurMinuten === 0) return;

    let id = cleanString(src.id, 80);
    if (!id || seenIds.has(id)) id = `log_${date.getTime()}_i${index}`;
    seenIds.add(id);

    const entry: LogEntry = {
      id,
      activityId: parseActivityId(src.activityId),
      naam: cleanString(src.naam) || 'Missie',
      type: normalizeType(typeof src.type === 'string' ? src.type : ''),
      duurMinuten,
      moment: typeof src.moment === 'string' ? src.moment : date.toISOString(),
      energie: cleanString(src.energie, 40) || 'Normaal',
    };
    const cat = typeof src.categorie === 'string' ? src.categorie.toLowerCase().trim() : '';
    if (CATEGORY_KEYS.includes(cat)) entry.categorie = getMainCategory(entry.type, entry.energie, cat);
    out.push(entry);
  });
  return out;
}

/** Nieuwe logregel met id 'log_<ts>_<rand>' en legacy-standaardwaarden voor ontbrekende velden. */
export function createLogEntry(input: Omit<LogEntry, 'id'>, now: number, random: () => number = Math.random): LogEntry {
  const rand = random().toString(36).substring(2, 7) || '0';
  const entry: LogEntry = {
    id: `log_${now}_${rand}`,
    activityId: input.activityId || null,
    naam: input.naam || 'Missie',
    type: input.type || 'Binnen',
    duurMinuten: Number(input.duurMinuten) || 30,
    moment: input.moment || new Date(now).toISOString(),
    energie: input.energie || 'Normaal',
  };
  if (input.categorie) entry.categorie = input.categorie;
  return entry;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function logCategory(log: Pick<LogEntry, 'type' | 'energie' | 'categorie'>): MainCategory {
  return getMainCategory(log.type, log.energie, log.categorie);
}

/**
 * Filtert zoals legacy: activiteit op naam (hoofdletterongevoelig), type, hoofdcategorie en periode.
 * 'week' = vanaf exact 7 dagen vóór `now`. Logs met een onleesbaar moment vallen nooit weg op periode.
 */
export function filterLogs(logs: LogEntry[], filters: LogFilters, now: Date): LogEntry[] {
  const activity = filters.activity !== 'all' ? filters.activity.trim().toLowerCase() : null;
  return logs.filter((log) => {
    if (activity !== null && (log.naam || '').trim().toLowerCase() !== activity) return false;
    if (filters.type !== 'all' && (log.type || '').toLowerCase() !== filters.type.toLowerCase()) return false;
    if (filters.category !== 'all' && logCategory(log) !== getMainCategory('', '', filters.category)) return false;

    if (filters.period !== 'all' && log.moment) {
      const logDate = new Date(log.moment);
      if (Number.isNaN(logDate.getTime())) return true;
      if (filters.period === 'today') {
        if (!isSameDay(logDate, now)) return false;
      } else if (filters.period === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (!isSameDay(logDate, yesterday)) return false;
      } else if (filters.period === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        if (logDate < weekAgo) return false;
      }
    }
    return true;
  });
}

const time = (iso: string) => {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? -Infinity : t;
};

/** Groepeert op naam (hoofdletterongevoelig); type/energie/categorie volgen de meest recente beurt. Nieuwste groep eerst. */
export function groupLogs(logs: LogEntry[]): LogGroup[] {
  const map = new Map<string, LogGroup>();
  for (const log of logs) {
    const key = (log.naam || 'Missie').trim().toLowerCase();
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        naam: log.naam || 'Missie',
        type: log.type || 'Binnen',
        energie: log.energie || 'Normaal',
        categorie: logCategory(log),
        activityId: log.activityId || null,
        count: 0,
        totalMinutes: 0,
        lastMoment: log.moment,
        sessions: [],
      };
      map.set(key, group);
    }
    group.count += 1;
    group.totalMinutes += Number(log.duurMinuten) || 0;
    group.sessions.push(log);
    if (time(log.moment) > time(group.lastMoment)) {
      group.lastMoment = log.moment;
      if (log.type) group.type = log.type;
      if (log.energie) group.energie = log.energie;
      group.categorie = logCategory({ type: group.type, energie: group.energie, categorie: log.categorie });
    }
  }
  return Array.from(map.values()).sort((a, b) => time(b.lastMoment) - time(a.lastMoment));
}

export function summarizeLogs(logs: LogEntry[]): LogSummary {
  const summary: LogSummary = {
    count: logs.length,
    totalMinutes: 0,
    binnen: 0,
    buiten: 0,
    byCategory: { 'buiten-hoog': 0, 'buiten-rustig': 0, 'binnen-hoog': 0, 'binnen-rustig': 0 },
  };
  for (const log of logs) {
    summary.totalMinutes += Number(log.duurMinuten) || 0;
    if ((log.type || '').toLowerCase() === 'buiten') summary.buiten += 1;
    else summary.binnen += 1;
    summary.byCategory[logCategory(log)] += 1;
  }
  return summary;
}

const TIME_FMT = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });
const DATE_FMT = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' });

/** "Vandaag om 14:05", "Gisteren om 09:10", "1 okt om 10:43"; ongeldig → "Eerder gedaan". */
export function formatMoment(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Eerder gedaan';
  const timeStr = TIME_FMT.format(date);
  if (isSameDay(date, now)) return `Vandaag om ${timeStr}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return `Gisteren om ${timeStr}`;
  return `${DATE_FMT.format(date)} om ${timeStr}`;
}

/** "45 min", "1 uur", "1u 15m". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const hours = Math.floor(m / 60);
  const rest = m % 60;
  return rest > 0 ? `${hours}u ${rest}m` : `${hours} uur`;
}
